/**
 * 排班模块公共工具函数
 */

const AVATAR_COLORS = ['#2563EB', '#10B981', '#7C3AED', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899']

/**
 * 根据 id 生成头像颜色
 */
export function getAvatarColor(id) {
  let hash = 0
  for (const c of String(id)) hash = (hash * 31 + c.charCodeAt(0)) & 0xffff
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

/**
 * 颜色转浅色背景（用于护工卡片背景）
 */
export function hexToLight(hex) {
  if (!hex || !hex.startsWith('#')) return '#EFF6FF'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgb(${Math.floor(r + (255 - r) * 0.88)},${Math.floor(g + (255 - g) * 0.88)},${Math.floor(b + (255 - b) * 0.88)})`
}

/**
 * 计算班次时长（含跨夜）
 * @param {string} start - 如 "06:00"
 * @param {string} end - 如 "14:00"
 * @returns {string} - 如 "8小时"
 */
export function calcDuration(start, end) {
  if (!start || !end) return ''
  const toMin = (t) => {
    const parts = t.split(':')
    return parseInt(parts[0]) * 60 + (parseInt(parts[1]) || 0)
  }
  let diff = toMin(end) - toMin(start)
  if (diff <= 0) diff += 24 * 60
  const h = Math.floor(diff / 60)
  const m = diff % 60
  return m ? `${h}小时${m}分` : `${h}小时`
}

/**
 * 获取星期标签
 * @param {string} date - YYYY-MM-DD
 * @returns {string} - 周一~周日
 */
export function getWeekLabel(date) {
  if (!date) return ''
  const labels = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  return labels[new Date(date.substring(0, 10)).getDay()]
}

/**
 * 生成日期列表数组
 * @param {string} rangeStart - YYYY-MM-DD
 * @param {string} rangeEnd - YYYY-MM-DD
 * @returns {Array} - [{ date, weekLabel, dayLabel }]
 */
export function buildWeekDays(rangeStart, rangeEnd) {
  const labels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
  const start = new Date(rangeStart)
  const end = new Date(rangeEnd)
  const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
  const result = []
  for (let i = 0; i < days; i++) {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    const dateStr = d.toISOString().substring(0, 10)
    const dayOfWeek = d.getDay()
    result.push({
      date: dateStr,
      weekLabel: labels[(dayOfWeek + 6) % 7],
      dayLabel: `${d.getMonth() + 1}/${d.getDate()}`
    })
  }
  return result
}

/**
 * 生成楼栋+楼层快捷按钮分组
 * @param {Array} roomList
 * @returns {Array} - [{ key, label, buildingId, floorNo }]
 */
export function buildRoomBuildingFloors(roomList) {
  const map = {}
  for (const r of roomList) {
    const bid = r.buildingId != null ? r.buildingId : 0
    const floor = r.floorNo != null ? r.floorNo : 0
    const key = `${bid}_${floor}`
    if (!map[key]) {
      map[key] = { key, buildingId: bid, floorNo: floor, buildingName: r.buildingName || '', label: '' }
    }
  }
  const buildingIds = new Set(roomList.map(r => r.buildingId != null ? r.buildingId : 0))
  const multiBuilding = buildingIds.size > 1
  const result = Object.values(map).sort((a, b) => {
    if (a.buildingId !== b.buildingId) return (a.buildingId || 0) - (b.buildingId || 0)
    return (a.floorNo || 0) - (b.floorNo || 0)
  })
  for (const item of result) {
    if (multiBuilding) {
      item.label = item.buildingName ? `${item.buildingName}${item.floorNo}层` : `${item.floorNo}层`
    } else {
      item.label = `${item.floorNo}层全部`
    }
  }
  return result
}

/**
 * 格式化日期显示
 * @param {string} val
 * @returns {string}
 */
export function fmtDate(val) {
  return val ? val.substring(0, 10) : ''
}

// ============================================================
// 日期工具函数（原生 JS 实现，替代 moment，无需额外依赖）
// ============================================================

/**
 * 将日期对象格式化为 YYYY-MM-DD 字符串
 * @param {Date} d
 * @returns {string}
 */
function _fmt(d) {
  var y = d.getFullYear()
  var m = d.getMonth() + 1
  var day = d.getDate()
  return y + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day)
}

/**
 * 获取今天的 YYYY-MM-DD 字符串
 * @returns {string}
 */
export function getTodayStr() {
  return _fmt(new Date())
}

/**
 * 获取某日期所在 ISO 周的周一（isoWeek start）
 * ISO 周：周一为第一天
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {string} - YYYY-MM-DD
 */
export function getIsoWeekStart(dateStr) {
  var d = new Date(dateStr)
  var day = d.getDay() // 0=周日, 1=周一, ..., 6=周六
  var diff = (day === 0 ? -6 : 1 - day) // 调整到周一
  d.setDate(d.getDate() + diff)
  return _fmt(d)
}

/**
 * 获取某日期所在 ISO 周的周日（isoWeek end）
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {string} - YYYY-MM-DD
 */
export function getIsoWeekEnd(dateStr) {
  var start = new Date(getIsoWeekStart(dateStr))
  start.setDate(start.getDate() + 6)
  return _fmt(start)
}

/**
 * 获取某日期所在月份的第一天
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {string} - YYYY-MM-DD
 */
export function getMonthStart(dateStr) {
  var d = new Date(dateStr)
  return d.getFullYear() + '-' + (d.getMonth() + 1 < 10 ? '0' : '') + (d.getMonth() + 1) + '-01'
}

/**
 * 获取某日期所在月份的最后一天
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {string} - YYYY-MM-DD
 */
export function getMonthEnd(dateStr) {
  var d = new Date(dateStr)
  var last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return _fmt(last)
}

/**
 * 日期加减天数
 * @param {string} dateStr - YYYY-MM-DD
 * @param {number} days - 正数加，负数减
 * @returns {string} - YYYY-MM-DD
 */
export function addDays(dateStr, days) {
  var d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return _fmt(d)
}

/**
 * 计算两个日期之间的天数差（end - start）
 * @param {string} startStr - YYYY-MM-DD
 * @param {string} endStr - YYYY-MM-DD
 * @returns {number}
 */
export function diffDays(startStr, endStr) {
  var s = new Date(startStr)
  var e = new Date(endStr)
  return Math.round((e - s) / (1000 * 60 * 60 * 24))
}

/**
 * 获取某日期所在 ISO 周的周次（isoWeek number）
 * @param {string} dateStr - YYYY-MM-DD
 * @returns {number}
 */
export function getIsoWeekNumber(dateStr) {
  var d = new Date(dateStr)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7)
  var week1 = new Date(d.getFullYear(), 0, 4)
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7)
}
